import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Home, ShoppingBag, Package, ShoppingCart, CreditCard, Mail,
  Lock, FileText, Navigation, ArrowUpRight, Shield, Truck,
  ScrollText, HelpCircle, CheckCircle, Clock, AlertCircle,
  ChevronRight, Eye, Rocket, Info, Pencil
} from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import Base from '../Base';

const PageDashboard = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [pageStatuses, setPageStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [publishLoading, setPublishLoading] = useState(false);
  const [templateInfo, setTemplateInfo] = useState(null);

  // Page definitions with clean metadata
  const pages = [
    // Core Pages
    { id: 'main', title: 'Home', description: 'Main landing page', icon: Home, category: 'core', priority: 'required' },
    { id: 'shop', title: 'Shop', description: 'Product catalog', icon: ShoppingBag, category: 'core', priority: 'required' },
    { id: 'product', title: 'Product', description: 'Product details', icon: Package, category: 'core', priority: 'required' },
    { id: 'cart', title: 'Cart', description: 'Shopping cart', icon: ShoppingCart, category: 'core', priority: 'required' },
    { id: 'checkout', title: 'Checkout', description: 'Payment flow', icon: CreditCard, category: 'core', priority: 'required' },
    { id: 'contact', title: 'Contact', description: 'Contact form', icon: Mail, category: 'core', priority: 'recommended' },
    { id: 'auth', title: 'Auth', description: 'Login & Signup', icon: Lock, category: 'core', priority: 'required' },
    { id: 'about', title: 'About', description: 'Brand story', icon: FileText, category: 'content', priority: 'recommended' },
    // Layout
    { id: 'navbar', title: 'Navbar', description: 'Navigation', icon: Navigation, category: 'layout', priority: 'required' },
    { id: 'footer', title: 'Footer', description: 'Page footer', icon: ArrowUpRight, category: 'layout', priority: 'required' },
    // Legal
    { id: 'privacy', title: 'Privacy', description: 'Privacy policy', icon: Shield, category: 'legal', priority: 'optional' },
    { id: 'shipping', title: 'Shipping', description: 'Delivery info', icon: Truck, category: 'legal', priority: 'optional' },
    { id: 'terms', title: 'Terms', description: 'Terms of service', icon: ScrollText, category: 'legal', priority: 'optional' },
    { id: 'faqs', title: 'FAQs', description: 'Common questions', icon: HelpCircle, category: 'legal', priority: 'optional' }
  ];

  useEffect(() => {
    fetchPageStatuses();
    fetchTemplateInfo();
  }, [templateId]);

  const fetchTemplateInfo = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      const response = await axios.get(`${API_BASE_URL}/api/template/selection`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success && response.data.has_selection) {
        setTemplateInfo(response.data.template);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/login');
      }
    }
  };

  const fetchPageStatuses = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      const response = await axios.get(`${API_BASE_URL}/api/template/${templateId}/dashboard/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPageStatuses(response.data.pages || {});
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/login');
        return;
      }
      const defaultStatuses = {};
      pages.forEach(page => {
        defaultStatuses[page.id] = { status: 'not_started', last_updated: null, is_published: false };
      });
      setPageStatuses(defaultStatuses);
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      not_started: { icon: AlertCircle, label: 'Not started', color: 'text-gray-400', bg: 'bg-gray-50', border: 'border-gray-200' },
      draft: { icon: Clock, label: 'In progress', color: 'text-[#D45614]', bg: 'bg-[#D45614]/5', border: 'border-[#D45614]/20' },
      complete: { icon: CheckCircle, label: 'Ready', color: 'text-green-500', bg: 'bg-green-50', border: 'border-green-200' },
      auto_generated: { icon: CheckCircle, label: 'Ready', color: 'text-green-500', bg: 'bg-green-50', border: 'border-green-200' },
      auto_filled: { icon: CheckCircle, label: 'Ready', color: 'text-green-500', bg: 'bg-green-50', border: 'border-green-200' }
    };
    return configs[status] || configs.not_started;
  };

  const calculateProgress = () => {
    if (!pageStatuses || Object.keys(pageStatuses).length === 0) return 0;
    const completedPages = Object.values(pageStatuses).filter(
      page => page && ['complete', 'auto_generated', 'auto_filled'].includes(page.status)
    ).length;
    return Math.round((completedPages / pages.length) * 100);
  };

  const handlePageClick = (pageId) => {
    const routes = {
      'main': `/template-builder/${templateId}/main`,
      'shop': `/template-builder/${templateId}/shop`,
      'product': `/template-builder/${templateId}/product`,
      'cart': `/template-builder/${templateId}/cart`,
      'checkout': `/template-builder/${templateId}/checkout`,
      'contact': `/template-builder/${templateId}/contact`,
      'auth': `/template-builder/${templateId}/auth`,
      'about': `/template-builder/${templateId}/about`,
      'navbar': `/template-builder/${templateId}/navbar`,
      'footer': `/template-builder/${templateId}/footer`,
      'privacy': `/template-builder/${templateId}/privacy`,
      'shipping': `/template-builder/${templateId}/shipping`,
      'terms': `/template-builder/${templateId}/terms`,
      'faqs': `/template-builder/${templateId}/faqs`,
    };
    if (routes[pageId]) navigate(routes[pageId]);
  };

  const handlePreview = () => window.open(`/template${templateId}`, '_blank', 'noopener,noreferrer');

  const [publishMessage, setPublishMessage] = useState(null);

  const handlePublish = async () => {
    setPublishLoading(true);
    setPublishMessage(null);
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        navigate('/login');
        return;
      }
      await axios.post(`${API_BASE_URL}/api/template/${templateId}/publish-all/`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPublishMessage({ type: 'success', text: 'Template published successfully!' });
      setTimeout(() => navigate('/homepage'), 1500);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/login');
        return;
      }
      setPublishMessage({ type: 'error', text: 'Error publishing template. Please try again.' });
    } finally {
      setPublishLoading(false);
    }
  };

  const progress = calculateProgress();
  const groupedPages = {
    core: pages.filter(p => p.category === 'core'),
    layout: pages.filter(p => p.category === 'layout'),
    content: pages.filter(p => p.category === 'content'),
    legal: pages.filter(p => p.category === 'legal')
  };

  // Page Card Component
  const PageCard = ({ page }) => {
    const pageStatus = pageStatuses[page.id] || { status: 'not_started' };
    const config = getStatusConfig(pageStatus.status);
    const StatusIcon = config.icon;
    const PageIcon = page.icon;
    const isComplete = ['complete', 'auto_generated', 'auto_filled'].includes(pageStatus.status);

    return (
      <div className={`group relative bg-white rounded-xl p-5 border ${config.border} hover:border-primary hover:shadow-md transition-all duration-200 text-left w-full`}>
        <div className="flex items-start justify-between mb-4">
          <div className={`w-10 h-10 ${config.bg} rounded-lg flex items-center justify-center`}>
            <PageIcon className={`w-5 h-5 ${config.color}`} />
          </div>
          <div className="flex items-center gap-2">
            {page.priority === 'required' && !isComplete && (
              <span className="text-xs font-medium text-[#D45614] bg-[#D45614]/10 px-2 py-0.5 rounded">Required</span>
            )}
            {isComplete && (
              <CheckCircle className="w-5 h-5 text-green-500" />
            )}
          </div>
        </div>

        <h3 className="text-base font-semibold text-gray-900 mb-1">{page.title}</h3>
        <p className="text-sm text-gray-500 mb-4">{page.description}</p>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <StatusIcon className={`w-4 h-4 ${isComplete ? 'text-green-500' : config.color}`} />
            <span className={`text-xs font-medium ${isComplete ? 'text-green-600' : config.color}`}>
              {isComplete ? 'Ready to edit' : config.label}
            </span>
          </div>

          {/* Edit button for all pages - since all are pre-filled */}
          <button
            onClick={() => handlePageClick(page.id)}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-all bg-[#D45614] text-white hover:bg-[#c04d12]"
          >
            <Pencil className="w-3 h-3" />
            {isComplete ? 'Edit' : 'Setup'}
          </button>
        </div>

        {pageStatus.last_updated && (
          <p className="text-xs text-gray-400 mt-3 pt-3 border-t border-gray-100">
            Updated {new Date(pageStatus.last_updated).toLocaleDateString()}
          </p>
        )}
      </div>
    );
  };

  // Section Component
  const Section = ({ title, badge, badgeColor, pages: sectionPages, columns = 3 }) => (
    <div className="mb-10">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        {badge && (
          <span className={`text-xs font-medium px-2 py-0.5 rounded ${badgeColor}`}>{badge}</span>
        )}
      </div>
      <div className={`grid grid-cols-1 md:grid-cols-2 ${columns === 4 ? 'lg:grid-cols-4' : columns === 2 ? 'lg:grid-cols-2' : 'lg:grid-cols-3'} gap-4`}>
        {sectionPages.map(page => <PageCard key={page.id} page={page} />)}
      </div>
    </div>
  );

  if (loading) {
    return (
      <Base Content={
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-sm text-gray-500">Loading configuration...</p>
          </div>
        </div>
      } />
    );
  }

  const DashboardContent = (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Template {templateId}</h1>
              <p className="text-sm text-gray-500 mt-0.5">Configure your pages</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handlePreview}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Eye className="w-4 h-4" />
                <span className="text-sm font-medium">Preview</span>
              </button>
              <button
                onClick={handlePublish}
                disabled={publishLoading || progress < 100}
                className="flex items-center gap-2 px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Rocket className="w-4 h-4" />
                <span className="text-sm font-medium">{publishLoading ? 'Publishing...' : 'Publish'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Section */}
      <div className="bg-white border-b border-gray-100">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-700">
              {Object.values(pageStatuses).filter(p => p && ['complete', 'auto_generated', 'auto_filled'].includes(p.status)).length} of {pages.length} pages complete
            </p>
            <span className="text-xl font-bold text-primary">{progress}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
            <div
              className="bg-primary h-full rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Publish Message */}
      {publishMessage && (
        <div className="px-6 pt-5">
          <div className={`rounded-xl p-4 flex items-center gap-3 ${publishMessage.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
            {publishMessage.type === 'success' ? <CheckCircle className="w-5 h-5 text-green-500" /> : <AlertCircle className="w-5 h-5 text-red-500" />}
            <p className="text-sm font-medium">{publishMessage.text}</p>
          </div>
        </div>
      )}

      {/* Template Info Banner */}
      {templateInfo && (
        <div className="px-6 pt-5">
          <div className="bg-[#D45614]/5 border border-[#D45614]/20 rounded-xl p-4 flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-[#D45614]" />
              <div>
                <p className="text-sm font-medium text-gray-900">Using {templateInfo.temp_name}</p>
                <p className="text-xs text-gray-600">
                  Selected {new Date(templateInfo.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/TemplateThemes?change=true')}
              className="text-sm font-medium text-[#D45614] hover:underline"
            >
              Change template
            </button>
          </div>
        </div>
      )}

      {/* Customization Notice Banner */}
      <div className="px-6 pt-4">
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Info className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">All pages have default sample content - Ready to customize!</p>
              <p className="text-sm text-gray-600 mt-1">
                Every page is pre-filled with placeholder content. Click <span className="font-medium text-amber-700">"Edit"</span> on any page to replace the defaults with your own:
              </p>
              <ul className="mt-2 text-sm text-gray-600 space-y-1">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                  <span><strong>Brand:</strong> Your business name, logo, tagline</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                  <span><strong>Theme:</strong> Colors and fonts that match your brand</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                  <span><strong>Content:</strong> Images, text, and product details</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="px-6 py-6">
        {/* Quick Start Guide */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-8 flex items-start gap-3">
          <Info className="w-5 h-5 text-[#D45614] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-gray-900">Quick Start</p>
            <p className="text-sm text-gray-600">
              Click any page to configure it. Required pages must be completed before publishing.
            </p>
          </div>
        </div>

        {/* Page Sections */}
        <Section
          title="Core Pages"
          badge="Required"
          badgeColor="bg-[#D45614]/10 text-[#D45614]"
          pages={groupedPages.core}
        />

        <Section
          title="Layout"
          badge="Required"
          badgeColor="bg-[#D45614]/10 text-[#D45614]"
          pages={groupedPages.layout}
          columns={2}
        />

        <Section
          title="Content"
          badge="Recommended"
          badgeColor="bg-[#D45614]/5 text-[#D45614]"
          pages={groupedPages.content}
          columns={2}
        />

        <Section
          title="Legal Pages"
          badge="Optional"
          badgeColor="bg-gray-100 text-gray-600"
          pages={groupedPages.legal}
          columns={4}
        />
      </main>
    </div>
  );

  return <Base Content={DashboardContent} />;
};

export default PageDashboard;
